/**
 * Docker image reference helpers (game-mgmt-refactor #7).
 *
 * Admins now enter a full image reference with a mutable tag
 * (e.g. `harbor.rehapiano.com/team/app:test-latest`) instead of a pinned
 * `@sha256:` digest. Validation is correspondingly looser: a tag OR a digest is
 * accepted, but the host/project/name structure is still required.
 */

// host[:port]/project/name[/more]:tag   OR   .../name@sha256:<64-hex>
const IMAGE_REF_RE =
  /^[a-z0-9.-]+(?::\d+)?\/[a-z0-9._-]+\/[a-z0-9._/-]+(?::[\w][\w.-]*|@sha256:[a-f0-9]{64})$/i

export function isValidImageRef(ref: string): boolean {
  return IMAGE_REF_RE.test((ref ?? '').trim())
}

export interface ParsedImageRef {
  host: string
  project: string
  repository: string
  reference: string
  isDigest: boolean
}

/** Split a full image reference into its parts, or null if unparseable. */
export function parseImageRef(ref: string): ParsedImageRef | null {
  const value = (ref ?? '').trim()
  if (!value) return null

  let base = value
  let reference = 'latest'
  let isDigest = false

  if (value.includes('@')) {
    const at = value.lastIndexOf('@')
    base = value.slice(0, at)
    reference = value.slice(at + 1)
    isDigest = true
  } else {
    const slash = value.lastIndexOf('/')
    const colon = value.lastIndexOf(':')
    if (colon > slash) {
      base = value.slice(0, colon)
      reference = value.slice(colon + 1)
    }
  }

  const parts = base.split('/')
  if (parts.length < 3) return null
  return {
    host: parts[0],
    project: parts[1],
    repository: parts.slice(2).join('/'),
    reference,
    isDigest,
  }
}

/**
 * If a tag carries the branch (e.g. `test-latest`), return the branch; else null.
 * Mirrors the device-side derivation so the UI can show a branch hint.
 */
export function branchFromTag(ref: string): string | null {
  const parsed = parseImageRef(ref)
  if (!parsed || parsed.isDigest) return null
  if (!parsed.reference.endsWith('-latest')) return null
  const branch = parsed.reference.slice(0, -'-latest'.length)
  return branch || null
}

/** Short, display-friendly digest (e.g. `sha256:abc1234…`). */
export function shortDigest(digest?: string | null): string {
  if (!digest) return '—'
  const d = digest.startsWith('sha256:') ? digest.slice('sha256:'.length) : digest
  return `sha256:${d.slice(0, 12)}`
}

/**
 * The repository part of a reference — everything before `:tag` / `@sha256:…`
 * (e.g. `harbor.rehapiano.com/team/flappy-hand`). Returns null if unparseable.
 */
export function repositoryOf(ref: string): string | null {
  const parsed = parseImageRef(ref)
  if (!parsed) return null
  return [parsed.host, parsed.project, parsed.repository].join('/')
}

/** True when the reference pins an immutable `@sha256:` digest. */
export function isPinnedDigest(ref: string): boolean {
  return parseImageRef(ref)?.isDigest === true
}

/**
 * Rebuild a reference against the same repository with another tag or digest.
 *
 * `withReference('host/p/n@sha256:…', 'test-latest')` -> `host/p/n:test-latest`
 * `withReference('host/p/n:test-latest', 'sha256:…')` -> `host/p/n@sha256:…`
 *
 * Returns null when the input cannot be parsed or the new reference is empty.
 */
export function withReference(ref: string, newReference: string): string | null {
  const repo = repositoryOf(ref)
  const next = (newReference ?? '').trim()
  if (!repo || !next) return null
  return next.startsWith('sha256:') ? `${repo}@${next}` : `${repo}:${next}`
}
